import React from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import ChartRenderer from "./ChartRenderer";
import EditableTitle from "./EditableTitle";
import { ADMIN_TYPES } from "./KpiSettingsModal";
import "../styles/TabContent.css";
import "../styles/KpiSettingsModal.css";
import generateChartData from "../../utils/generateChartData";
import KpiTable from "../KpiTable";
import CrosstabTable from "../../components/CrosstabTable";

export default function TabContent({
  selectedTab,
  updateKPI,
  updateFilter,
  updateFilterMultiple,
  removeFilter,
  removeKPI,
  reorderKPIs,
  updateFilterTitle,
  filteredRows,
  toggleKPIViewMode,
  crosstabDisplayData,
  setCrosstabDisplayData,
  onOpenKpiSettings,
  selectedQuestionKey,
  onAddKPIBanner,
  onRemoveKPIBanner,
  onRemoveTabBanner,
}) {
  if (!selectedTab) {
    return (
      <div className="tab-content-empty">
        <p>No tab selected</p>
      </div>
    );
  }

  const safeString = (v, fb = "") => {
    if (typeof v === "string") return v;
    if (typeof v === "number") return String(v);
    if (!v) return fb;
    if (typeof v === "object") return v.Name || v.name || v.label || v.title || fb;
    return fb;
  };

  const handleKpiDragEnd = (result) => {
    if (!result.destination) return;
    if (result.destination.index === result.source.index) return;
    reorderKPIs && reorderKPIs(selectedTab.id, result.source.index, result.destination.index);
  };

  return (
    <div className="tab-content">
      <div className="tab-content-layout">

        {/* ── Local filters + Tab banners panel ── */}
        {(selectedTab.filters?.length > 0 || selectedTab.banners?.length > 0) && (
          <div className="filters-left">

            {/* Tab banners */}
            {selectedTab.banners?.length > 0 && (
              <div className="tab-banner-section">
                <h4 className="section-title">📊 Tab Banners</h4>
                <div className="filters-single-card" style={{ marginBottom: "12px" }}>
                  {selectedTab.banners.map((b) => (
                    <div key={b.key} className="banner-chip">
                      <span className="banner-chip-label">{b.label || b.key}</span>
                      <button
                        className="removefilter-btn"
                        onClick={() => onRemoveTabBanner && onRemoveTabBanner(selectedTab.id, b.key)}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Local filters */}
            {selectedTab.filters?.length > 0 && (
              <>
                <h4 className="section-title">🎯 Filters</h4>
                <div className="filters-single-card">
                  {selectedTab.filters.map((f) => {
                    const filterLabel = safeString(f.dashboardTitle || f.label, f.variable_name || "Filter");
                    const isMulti = f.multiple === 1;
                    const selectedArr = Array.isArray(f.selectedValue)
                      ? f.selectedValue
                      : f.selectedValue
                      ? [f.selectedValue]
                      : [];

                    return (
                      <div key={f.id} className="filter-item">
                        <div className="filter-header">
                          <EditableTitle
                            className="filter-label"
                            value={filterLabel}
                            onSave={(title) => updateFilterTitle && updateFilterTitle(selectedTab.id, f.id, title)}
                          />
                          <button
                            className="removefilter-btn"
                            onClick={() => removeFilter(selectedTab.id, f.id)}
                          >
                            ×
                          </button>
                        </div>

                        <label className="filter-multiselect-toggle">
                          <input
                            type="checkbox"
                            checked={isMulti}
                            onChange={() =>
                              updateFilterMultiple && updateFilterMultiple(selectedTab.id, f.id)
                            }
                          />
                          Multi-select
                        </label>

                        {isMulti ? (
                          <select
                            multiple
                            className="filter-select"
                            value={selectedArr}
                            onChange={(e) => {
                              const chosen = Array.from(e.target.selectedOptions).map((o) => o.value);
                              updateFilter(selectedTab.id, f.id, chosen);
                            }}
                            style={{ minHeight: "90px" }}
                          >
                            {f.options?.map((opt, index) => {
                              const optionKey = typeof opt === "string" ? opt : opt.Id || opt.id || index;
                              const optionValue = typeof opt === "string" ? opt : safeString(opt.Value || opt.value || opt.Name) || `value_${index}`;
                              const optionLabel = typeof opt === "string" ? opt : safeString(opt.Name || opt.name || opt.label) || `Option ${index + 1}`;
                              return <option key={optionKey} value={optionValue}>{optionLabel}</option>;
                            })}
                          </select>
                        ) : (
                          <select
                            className="filter-select"
                            value={Array.isArray(f.selectedValue) ? (f.selectedValue[0] ?? "") : (f.selectedValue || "")}
                            onChange={(e) => updateFilter(selectedTab.id, f.id, e.target.value)}
                          >
                            <option value="">All</option>
                            {f.options?.map((opt, index) => {
                              const optionKey = typeof opt === "string" ? opt : opt.Id || opt.id || index;
                              const optionValue = typeof opt === "string" ? opt : safeString(opt.Value || opt.value || opt.Name) || `value_${index}`;
                              const optionLabel = typeof opt === "string" ? opt : safeString(opt.Name || opt.name || opt.label) || `Option ${index + 1}`;
                              return <option key={optionKey} value={optionValue}>{optionLabel}</option>;
                            })}
                          </select>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}

        {/* ── KPI grid ── */}
        <div style={{ flex: 1, overflow: "auto" }}>
          {selectedTab.kpis?.length === 0 ? (
            <div className="tab-content-empty">
              <p>Add KPIs to visualize data.</p>
            </div>
          ) : (
            <DragDropContext onDragEnd={handleKpiDragEnd}>
              <Droppable droppableId="kpis-grid" direction="horizontal">
                {(droppableProvided) => (
                  <div
                    className="kpis-grid"
                    ref={droppableProvided.innerRef}
                    {...droppableProvided.droppableProps}
                  >
                    {selectedTab.kpis.map((kpi, index) => {
                      const isNumeric = !!kpi.isNumeric;
                      const kpiData = kpi.data;
                      const kpiSettings = kpi.settings || {};

                      return (
                        <Draggable key={kpi.id} draggableId={kpi.id} index={index}>
                          {(dragProvided, dragSnapshot) => (
                            <div
                              ref={dragProvided.innerRef}
                              {...dragProvided.draggableProps}
                              className={`kpi-card${dragSnapshot.isDragging ? " kpi-card--dragging" : ""}`}
                            >

                              {/* ── KPI Header ── */}
                              <div className="kpi-header">
                                <span
                                  className="kpi-drag-handle"
                                  title="Drag to reposition"
                                  {...dragProvided.dragHandleProps}
                                >
                                  ⠿
                                </span>
                                <h5 className="kpi-title">
                                  <EditableTitle
                                    className="kpi-qtile"
                                    value={safeString(kpi.dashboardTitle || kpi.qTitle)}
                                    onSave={(title) => updateKPI(selectedTab.id, kpi.id, "dashboardTitle", title)}
                                  />
                                </h5>
                                <div className="kpi-header-actions">
                                  {/* Add banner button */}
                                  <button
                                    className="kpi-banner-btn"
                                    title={selectedQuestionKey ? "Add selected variable as KPI Banner" : "Select a variable in the sidebar first"}
                                    onClick={() => onAddKPIBanner && onAddKPIBanner(selectedTab.id, kpi.id)}
                                  >
                                    📊
                                  </button>
                                  {/* Settings gear */}
                                  <button
                                    className="kpi-settings-btn"
                                    title="KPI Settings"
                                    onClick={() => onOpenKpiSettings && onOpenKpiSettings(selectedTab.id, kpi.id)}
                                  >
                                    ⚙
                                  </button>
                                  {/* Remove */}
                                  <button
                                    className="remove-btn"
                                    onClick={() => removeKPI(selectedTab.id, kpi.id)}
                                  >
                                    ×
                                  </button>
                                </div>
                              </div>

                              {/* ── KPI Banners ── */}
                              {kpi.banners?.length > 0 && (
                                <div className="kpi-banners-row">
                                  {kpi.banners.map((b) => (
                                    <div key={b.key} className="kpi-banner-chip">
                                      <span className="kpi-banner-chip-label">{b.label || b.key}</span>
                                      <button
                                        className="kpi-banner-chip-remove"
                                        onClick={() => onRemoveKPIBanner && onRemoveKPIBanner(selectedTab.id, kpi.id, b.key)}
                                      >
                                        ×
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* ── KPI Controls (chart type only) ── */}
                              {!isNumeric && (
                                <div className="kpi-controls">
                                  <label className="control-label">Chart:</label>
                                  <select
                                    className="control-select"
                                    value={kpi.type}
                                    onChange={(e) => updateKPI(selectedTab.id, kpi.id, "type", e.target.value)}
                                  >
                                    {kpi.isCrosstab ? (
                                      <optgroup label="Crosstab Charts">
                                        <option value="stackedcolumn">Stacked Column</option>
                                        <option value="stackedbar">Stacked Bar</option>
                                        <option value="groupedcolumn">Grouped Column</option>
                                        <option value="groupedbar">Grouped Bar</option>
                                        <option value="line">Line</option>
                                      </optgroup>
                                    ) : (
                                      <>
                                        <optgroup label="Basic">
                                          <option value="bar">Bar</option>
                                          <option value="column">Column</option>
                                          <option value="line">Line</option>
                                          <option value="area">Area</option>
                                        </optgroup>
                                        <optgroup label="Stacked">
                                          <option value="stackedbar">Stacked Bar</option>
                                          <option value="stackedcolumn">Stacked Column</option>
                                        </optgroup>
                                        <optgroup label="Circular">
                                          <option value="pie">Pie</option>
                                          <option value="doughnut">Doughnut</option>
                                          <option value="radialbar">Radial Bar</option>
                                          <option value="radar">Radar</option>
                                        </optgroup>
                                        <optgroup label="Advanced">
                                          <option value="scatter">Scatter</option>
                                          <option value="composed">Composed</option>
                                          <option value="funnel">Funnel</option>
                                        </optgroup>
                                        <optgroup label="Pictogram">
                                          <option value="pictogram">Pictogram</option>
                                        </optgroup>
                                      </>
                                    )}
                                  </select>

                                  {!kpi.isCrosstab && kpi.type === "pictogram" && (
                                    <>
                                      <label className="control-label">Icon:</label>
                                      <select
                                        className="control-select"
                                        value={kpi.adminType || ""}
                                        onChange={(e) => updateKPI(selectedTab.id, kpi.id, "adminType", e.target.value)}
                                      >
                                        <option value="" disabled>Select admin type…</option>
                                        {ADMIN_TYPES.map(({ value, label }) => (
                                          <option key={value} value={value}>{label}</option>
                                        ))}
                                      </select>
                                    </>
                                  )}
                                </div>
                              )}

                              {/* ── KPI Chart / Table ── */}
                              <div id={`kpi-${kpi.id}`} className="kpi-preview">
                                {isNumeric ? (
                                  <KpiTable data={kpiData} numericSummary />

                                ) : kpi.isCrosstab ? (
                                  kpi.viewMode === "chart" ? (
                                    <ChartRenderer
                                      type={kpi.type || "stackedcolumn"}
                                      data={kpiData}
                                      width="100%"
                                      height={400}
                                      isCrosstab={true}
                                    />
                                  ) : (
                                    <CrosstabTable crosstab={crosstabDisplayData?.crosstabData} />
                                  )

                                ) : (
                                  <>
                                    {(() => {
                                      const dynamicWidth = Math.max(900, (kpiData?.length || 1) * 120);
                                      return (
                                        <ChartRenderer
                                          type={kpi.type}
                                          data={kpiData}
                                          width={`${dynamicWidth}px`}
                                          height={400}
                                          kpi={kpi}
                                        />
                                      );
                                    })()}

                                    {kpiSettings.showTable && (
                                      <div style={{ marginTop: "16px" }}>
                                        <KpiTable data={kpiData} />
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>

                            </div>
                          )}
                        </Draggable>
                      );
                    })}
                    {droppableProvided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          )}
        </div>
      </div>
    </div>
  );
}
